/**
 * 2026-04-25 Phase 32-C.6 — 제련/각인 성공 직후 shimmer 감지 훅.
 *
 * 백엔드 응답을 받은 모달이 `markForgeShimmer(instanceId)` 를 호출하면,
 * 해당 instanceId 의 카드/슬롯이 1회 shimmer 애니메이션을 재생.
 *
 * 저장소: sessionStorage (새로고침하면 초기화). 만료 시간 10초.
 * 여러 인스턴스 동시 가능.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "forgeShimmer";
const TTL_MS = 10_000;
const SHIMMER_EVENT = "forge-shimmer-changed";

export type ForgeShimmerKind = "refine" | "engrave";

type Entry = { instanceId: string; expiresAt: number; kind: ForgeShimmerKind };

function readAll(): Entry[] {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return [];
        const now = Date.now();
        return arr.filter((e: Entry) => e && typeof e.instanceId === "string" && e.expiresAt > now);
    } catch {
        return [];
    }
}

function writeAll(entries: Entry[]) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {}
    window.dispatchEvent(new CustomEvent(SHIMMER_EVENT));
}

/**
 * 제련/각인 성공 응답 받으면 호출 → 해당 instanceId 10초 shimmer 큐에.
 * 모달의 onComplete 안에서 사용.
 */
export function markForgeShimmer(instanceId: string, kind: ForgeShimmerKind = "refine") {
    if (!instanceId) return;
    const existing = readAll().filter(e => e.instanceId !== instanceId);
    existing.push({ instanceId, expiresAt: Date.now() + TTL_MS, kind });
    writeAll(existing);
}

/** 특정 instanceId 가 현재 shimmer 중인지 감지. */
export function useForgeShimmer(instanceId?: string | null): { active: boolean; kind: ForgeShimmerKind | null } {
    const [state, setState] = useState<{ active: boolean; kind: ForgeShimmerKind | null }>({ active: false, kind: null });

    useEffect(() => {
        if (!instanceId) return;
        const check = () => {
            const entries = readAll();
            const found = entries.find(e => e.instanceId === instanceId);
            if (found) {
                setState({ active: true, kind: found.kind });
                const remaining = found.expiresAt - Date.now();
                const delay = Math.min(remaining, 1800);  // shimmer 재생 시간
                const id = setTimeout(() => {
                    setState({ active: false, kind: null });
                    // 재생 끝나면 큐에서 제거
                    const fresh = readAll().filter(e => e.instanceId !== instanceId);
                    writeAll(fresh);
                }, delay);
                return () => clearTimeout(id);
            } else {
                setState({ active: false, kind: null });
            }
            return undefined;
        };
        const cleanup = check();
        const handler = () => { cleanup?.(); check(); };
        window.addEventListener(SHIMMER_EVENT, handler);
        return () => {
            cleanup?.();
            window.removeEventListener(SHIMMER_EVENT, handler);
        };
    }, [instanceId]);

    return state;
}
